import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import { useState } from "react";

import {
	BILL_PAYMENT_SETTLE_REFUSALS,
	BILL_PAYMENT_TRANSFERRED_NOTE,
	blocksSettlingBill,
	PAYMENT_DOCUMENT_NOUNS,
	type DocumentPayment,
	type PaymentDocument,
	type PaymentDocumentDetail,
	type Settlement,
	type SettlementReason,
	type SettlementRequest,
} from "@ravnar/api-contract";

import { billPaymentQueryOptions } from "../../../api/bill-payments";
import { paymentDocumentQueryOptions, reopenPaymentDocument, settlePaymentDocument } from "../../../api/payments";
import { useAskAgain } from "../../../api/refresh";
import { useSession } from "../../../api/session";
import { Button } from "../../../components/ui/button";
import { Drawer } from "../../../components/ui/drawer";
import { Field, Select, Textarea } from "../../../components/ui/field";
import { Pill } from "../../../components/ui/pill";
import { Skeleton } from "../../../components/ui/skeleton";
import {
	PAYMENT_DOCUMENT_KIND_LABELS,
	PAYMENT_STATUS_LABELS,
	PAYMENT_STATUS_TONES,
	SETTLEMENT_NOTE_MAX_LENGTH,
	SETTLEMENT_REASON_LABELS,
	SETTLEMENT_REASONS,
} from "../../../domain/payments";
import { formatAmount, formatRelativeTime, formatShortDate } from "../../../lib/format";
import { showToast } from "../../../lib/toast";
import { DrawerSection } from "./drawer-section";
import { PayWithRevolut } from "./pay-with-revolut";

interface PaymentDocumentDrawerProps {
	/** The document the drawer shows; the drawer is closed while there is none. */
	readonly fileId: string | undefined;
	readonly onClose: () => void;
}

/**
 * One bill or invoice of the year on the side: what Document Filing read of it and where it stands, the bank
 * transactions that pay it, and — for an editor of Finance — the way to settle it by hand while nothing pays it, or to
 * reopen what a person settled.
 */
export function PaymentDocumentDrawer({ fileId, onClose }: PaymentDocumentDrawerProps) {
	const { data, isPending, isError, error } = useQuery({
		...paymentDocumentQueryOptions(fileId ?? ""),
		enabled: fileId !== undefined,
	});
	return (
		<Drawer
			open={fileId !== undefined}
			title={data === undefined ? "Document" : PAYMENT_DOCUMENT_KIND_LABELS[data.document.kind]}
			onClose={onClose}
		>
			{fileId === undefined ? null : isPending ? (
				<div role="status" aria-label="Loading the document" className="flex flex-col gap-3">
					<Skeleton className="h-3.5 w-2/3" />
					<Skeleton className="h-3.5 w-1/2" />
					<Skeleton className="h-3.5 w-3/5" />
				</div>
			) : isError ? (
				<p role="alert" className="text-caption text-alert">
					{error.message}
				</p>
			) : (
				<DocumentDetail detail={data} />
			)}
		</Drawer>
	);
}

/**
 * A settlement sent for the document, or what settles it taken back: the answer is its new standing, which every query
 * of Payments — the drawer's too — is asked again for, and the audit log once its entry has arrived. A change the API
 * refused asks again too: a 409 says a transaction pays the document since the drawer read it.
 */
function useSettlement(document: PaymentDocument) {
	const queryClient = useQueryClient();
	const askAgain = useAskAgain();
	const noun = PAYMENT_DOCUMENT_NOUNS[document.kind];
	const settle = () =>
		Promise.all([queryClient.invalidateQueries({ queryKey: ["payments"] }), askAgain(["settings", "audit-log"])]);
	const save = useMutation({
		mutationFn: (request: SettlementRequest) => settlePaymentDocument(document.fileId, request),
		onSuccess: async () => {
			await settle();
			showToast(`${noun} settled.`);
		},
		onError: () => queryClient.invalidateQueries({ queryKey: ["payments"] }),
	});
	const reopen = useMutation({
		mutationFn: () => reopenPaymentDocument(document.fileId),
		onSuccess: async () => {
			await settle();
			showToast(`${noun} reopened.`);
		},
		onError: () => queryClient.invalidateQueries({ queryKey: ["payments"] }),
	});
	return { save, reopen, busy: save.isPending || reopen.isPending, error: save.error ?? reopen.error };
}

function DocumentDetail({ detail }: { readonly detail: PaymentDocumentDetail }) {
	const { member } = useSession();
	const canEdit = member.access.finance === "editor";
	const { document, payments, year } = detail;
	const { save, reopen, busy, error } = useSettlement(document);
	const unpaid = document.status === "open" || document.status === "overdue";
	// A bill whose draft may still be paid in Revolut is not settled by hand: it would be paid twice. The Pay with Revolut
	// section reads the same query.
	const { data: revolut } = useQuery({
		...billPaymentQueryOptions(document.fileId),
		enabled: document.kind === "vendor-invoice",
	});
	const draft = revolut?.billPayment;
	const live = draft === undefined || draft === null || !blocksSettlingBill(draft.status) ? undefined : draft.status;
	return (
		<div className="flex flex-col gap-5">
			<DocumentFacts document={document} />
			{error === null ? null : (
				<p role="alert" className="text-caption text-alert">
					{error.message}
				</p>
			)}
			<PaymentsSection payments={payments} year={year} />
			{document.kind === "vendor-invoice" ? <PayWithRevolut document={document} /> : null}
			{document.status === "settled" && document.settlement !== undefined ? (
				<SettlementSection
					settlement={document.settlement}
					canEdit={canEdit}
					busy={busy}
					onReopen={() => {
						reopen.mutate();
					}}
				/>
			) : null}
			{unpaid && canEdit && live !== undefined ? (
				<DrawerSection title="Settle by hand">
					<p className="text-caption text-ink-3">{BILL_PAYMENT_SETTLE_REFUSALS[live]}</p>
				</DrawerSection>
			) : null}
			{unpaid && canEdit && live === undefined ? (
				<SettleForm
					busy={busy}
					{...(draft?.status === "completed" && { notice: BILL_PAYMENT_TRANSFERRED_NOTE })}
					onSave={(reason, note) => {
						save.mutate({ reason, ...(note !== "" && { note }) });
					}}
				/>
			) : null}
		</div>
	);
}

/** What Document Filing read of the document, a line each, where it stands, and the way to its page in Intake. */
function DocumentFacts({ document }: { readonly document: PaymentDocument }) {
	const { fileId, fileName, total, counterparty, number, issuedOn, dueOn, status } = document;
	return (
		<dl className="grid grid-cols-[96px_minmax(0,1fr)] items-center gap-x-4 gap-y-2.5 text-body">
			<dt className="text-ink-3">Amount</dt>
			<dd className="text-ink tabular-nums">{formatAmount(total.amount, total.currency)}</dd>
			{counterparty === undefined ? null : (
				<>
					<dt className="text-ink-3">With</dt>
					<dd className="text-ink">{counterparty}</dd>
				</>
			)}
			{number === undefined ? null : (
				<>
					<dt className="text-ink-3">Number</dt>
					<dd className="text-ink">{number}</dd>
				</>
			)}
			{issuedOn === undefined ? null : (
				<>
					<dt className="text-ink-3">Dated</dt>
					<dd className="text-ink">{formatShortDate(issuedOn)}</dd>
				</>
			)}
			<dt className="text-ink-3">Due</dt>
			<dd className={status === "overdue" ? "text-alert" : "text-ink"}>
				{dueOn === undefined ? "No due day" : formatShortDate(dueOn)}
			</dd>
			<dt className="text-ink-3">Status</dt>
			<dd>
				<Pill tone={PAYMENT_STATUS_TONES[status]}>{PAYMENT_STATUS_LABELS[status]}</Pill>
			</dd>
			<dt className="text-ink-3">Document</dt>
			<dd className="min-w-0">
				<Link
					to="/intake/$fileId"
					params={{ fileId }}
					className="block font-medium break-all text-ink hover:underline"
				>
					{fileName}
				</Link>
			</dd>
		</dl>
	);
}

/**
 * The bank transactions matched to the document, the latest booked first, each opening in the ledger's drawer while it
 * is of this year's ledger; or that none pays it yet.
 */
function PaymentsSection({ payments, year }: { readonly payments: readonly DocumentPayment[]; readonly year: number }) {
	return (
		<DrawerSection title="Payments">
			{payments.length === 0 ? (
				<p className="text-caption text-ink-3">
					No bank transaction pays it yet. It is paid once reconciliation matches its payment to it.
				</p>
			) : (
				<ul aria-label="Payments">
					{payments.map(payment => (
						<PaymentRow key={payment.transactionId} payment={payment} inLedger={payment.year === year} />
					))}
				</ul>
			)}
		</DrawerSection>
	);
}

function PaymentRow({ payment, inLedger }: { readonly payment: DocumentPayment; readonly inLedger: boolean }) {
	const { transactionId, description, bookedOn, account, matchedBy, amount } = payment;
	return (
		<li className="flex items-start gap-3 border-b border-line py-2.5 last:border-0">
			<span className="min-w-0 flex-1">
				{inLedger ? (
					<Link
						to="/finance/reconciliation"
						search={{ transaction: transactionId }}
						className="block text-ctl-lg font-medium text-ink hover:underline"
					>
						{description}
					</Link>
				) : (
					<span className="block text-ctl-lg font-medium text-ink">{description}</span>
				)}
				<span className="block text-caption text-ink-3">
					{`${formatShortDate(bookedOn)} · ${account.name} · matched by ${matchedBy}`}
				</span>
			</span>
			<span className="shrink-0 text-body whitespace-nowrap text-ink tabular-nums">
				{formatAmount(amount.amount, amount.currency)}
			</span>
		</li>
	);
}

/** What settles the document by hand, who said so and when, and — for an editor — the way to take it back. */
function SettlementSection({
	settlement,
	canEdit,
	busy,
	onReopen,
}: {
	readonly settlement: Settlement;
	readonly canEdit: boolean;
	readonly busy: boolean;
	readonly onReopen: () => void;
}) {
	return (
		<DrawerSection title="Settled by hand">
			<p className="text-body text-ink-2">{SETTLEMENT_REASON_LABELS[settlement.reason]}</p>
			{settlement.note === undefined ? null : <p className="text-body text-ink">{settlement.note}</p>}
			<p className="text-caption text-ink-3">{`By ${settlement.by}, ${formatRelativeTime(settlement.at)}`}</p>
			{canEdit ? (
				<div className="flex items-center gap-3">
					<Button size="sm" disabled={busy} onClick={onReopen}>
						Reopen
					</Button>
					<span className="text-caption text-ink-3">It is to be paid again, until something pays it.</span>
				</div>
			) : null}
		</DrawerSection>
	);
}

/** Settles the document by hand, why and with what note; the reason must be chosen first. */
function SettleForm({
	busy,
	notice,
	onSave,
}: {
	readonly busy: boolean;
	/** What to know before settling it, above the form: that Revolut transferred the bill's draft. */
	readonly notice?: string;
	readonly onSave: (reason: SettlementReason, note: string) => void;
}) {
	const [reason, setReason] = useState<SettlementReason | "">("");
	const [note, setNote] = useState("");
	return (
		<DrawerSection title="Settle by hand">
			{notice === undefined ? null : <p className="text-caption text-ink-2">{notice}</p>}
			<p className="text-caption text-ink-3">
				Paid another way, cancelled by a credit note, or nothing to pay: it leaves what is owed.
			</p>
			<form
				className="flex flex-col gap-2.5"
				onSubmit={event => {
					event.preventDefault();
					if (reason !== "") {
						onSave(reason, note.trim());
					}
				}}
			>
				<Field label="Reason">
					<Select
						value={reason}
						onChange={event => {
							const chosen = SETTLEMENT_REASONS.find(option => option === event.target.value);
							setReason(chosen ?? "");
						}}
					>
						<option value="" disabled>
							Choose a reason
						</option>
						{SETTLEMENT_REASONS.map(option => (
							<option key={option} value={option}>
								{SETTLEMENT_REASON_LABELS[option]}
							</option>
						))}
					</Select>
				</Field>
				<Field label="Note">
					<Textarea
						maxLength={SETTLEMENT_NOTE_MAX_LENGTH}
						value={note}
						onChange={event => {
							setNote(event.target.value);
						}}
					/>
				</Field>
				<div>
					<Button type="submit" size="sm" disabled={busy || reason === ""}>
						Settle
					</Button>
				</div>
			</form>
		</DrawerSection>
	);
}
